export type Validator = (value: any) => true | string;

export const allowEmpty =
	(validator: Validator): Validator =>
	(v) => {
		if (v === null || v === undefined || v === "") return true;
		return validator(v);
	};

export function isRowEmpty<Row>(
	row: Partial<Row>,
	keys: (keyof Row)[],
): boolean {
	return keys.every((k) => row[k] == null);
}

export function stripSlash(address: string): string {
	return address.replace(/\/+$/, "");
}

export function parseBackupCodes(codes: string): string[] {
	return codes
		.split(",")
		.map((c) => c.trim())
		.filter(Boolean);
}

export type AuthGroups = Readonly<Record<string, Readonly<string[]>>>;

export interface Cell {
	value: any;
	disabled: boolean;
	isValid: boolean;
}

export type Row<C extends string> = {
	[K in C]: Cell;
} & {
	isRowValid: boolean;
};

export type AuthCols<T> = {
	[group: string]: readonly (keyof T)[];
};

export function applyDisabledFlagsForRow<
	T extends Record<string, Cell | boolean>,
	G extends AuthCols<T>,
>(row: T, authCols: G): void {
	const groupEntries = Object.entries(authCols) as [
		keyof G,
		readonly (keyof T)[],
	][];
	const hasValue = (cell: Cell): boolean =>
		cell.value !== null && cell.value !== undefined && cell.value !== "";

	let activeGroup: keyof G | null = null;
	outer: for (const [group, cols] of groupEntries) {
		for (const col of cols) {
			if (typeof row[col] !== "boolean" && hasValue(row[col])) {
				activeGroup = group;
				break outer;
			}
		}
	}

	if (activeGroup === null) {
		for (const [, cols] of groupEntries) {
			for (const col of cols) {
				if (typeof row[col] !== "boolean") row[col].disabled = false;
			}
		}
		return;
	}

	for (const [group, cols] of groupEntries) {
		const isActive = group === activeGroup;
		for (const col of cols) {
			if (typeof row[col] !== "boolean") row[col].disabled = !isActive;
		}
	}
}

export function applyRowIsValid<C extends string>(row: Row<C>): void {
	const cellKeys = (Object.keys(row) as Array<keyof Row<C>>).filter(
		(k): k is C => k !== "isRowValid",
	);

	row.isRowValid = cellKeys.every((col) => {
		if (row[col].disabled) return true;
		return row[col].isValid;
	});
}
